import { Text, View, StyleSheet } from "@react-pdf/renderer";
import { formatNumber } from "../../../services/mappings";
import { renderNumberCell } from "../../../services/pdfService";

const styles = StyleSheet.create({
  table: {
    fontSize: 8,
    marginTop: 5,
  },
  row: {
    flexDirection: "row",
  },
  blankCell: {
    paddingVertical: 2,
    paddingHorizontal: 2,
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
  },
  cell: {
    border: "1pt solid #000",
    paddingVertical: 2,
    paddingHorizontal: 2,
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
  },
  // Primera fila (grupos)
  hGroup: {
    fontWeight: "bold",
    fontSize: 8,
  },
  // Celda que ocupa 2 filas (Item, Descripción, Unidad)
  rowSpanCell: {
    paddingTop: 9,
    paddingBottom: 9,
    fontWeight: "bold",
  },

  // Anchos base (suman ~100)
  wItem: { width: "5%" },
  wDesc: { width: "14%" },
  wUnidad: { width: "7%" },

  // Según contrato (3)
  wScCant: { width: "5%" },
  wScPU: { width: "7%" },
  wScTot: { width: "8%" },
  wScGroup: { width: "20%" },

  // Acumulado mes anterior (2)
  wAmaCant: { width: "7%" },
  wAmaTot: { width: "8%" },
  wAmaGroup: { width: "15%" },

  // Período actual (5)
  wPaCant: { width: "4%" },
  wPaVmb: { width: "6%" },
  wPaFr: { width: "5%" },
  wPaMr: { width: "5%" },
  wPaTot: { width: "6%" },
  wPaGroup: { width: "26%" },

  // Total acumulado (2)
  wTaCant: { width: "5.5%" },
  wTaTot: { width: "7.5%" },
  wTaGroup: { width: "13%" },

  noRightBorder: {
    borderRight: 0,
  },
  dataRow: {
    flexDirection: "row",
  },
  dataCell: {
    border: "0.5px solid #000",
    paddingVertical: 2,
    paddingHorizontal: 2,
    justifyContent: "center",
    textAlign: "center",
  },
  descCell: {
    textAlign: "left",
  },
  mainRowText: {
    fontWeight: "bold",
  },
  subRowDescText: {
    paddingLeft: 8,
  },
  numberText: {
    fontSize: 8,
  },
  numberTextMillion: {
    fontSize: 7,
  },
  numberTextBillion: {
    fontSize: 6,
  },
  numberHundredMillion: {
    fontSize: 6,
  },
  numberTextStretch: {
    fontSize: 8,
  },
  numberTextMillionStretch: {
    fontSize: 5,
  },
  numberTextBillionStretch: {
    fontSize: 4,
  },
  numberHundredMillionStretch: {
    fontSize: 3,
  },
});

function ItemsTable({ items, hashRows = [] }) {
  const rows = items?.data ?? [];

  const renderDataRow = (item, isSubPartida = false) => {
    const detalle = item?.detalle;
    const textStyle = isSubPartida ? undefined : styles.mainRowText;

    return (
      <View
        key={`${item.partidA_ID}-${item.subpartidA_ID ?? "main"}-${item.id}`}
        style={[
          styles.dataRow,
          !isSubPartida ? { backgroundColor: "#f0f0f0" } : undefined,
        ]}
        wrap={false}
      >
        <View style={[styles.dataCell, styles.wItem]}>
          <Text style={textStyle}>{item?.codigo ?? ""}</Text>
        </View>

        <View style={[styles.dataCell, styles.wDesc]}>
          <Text
            style={[
              styles.descCell,
              textStyle,
              isSubPartida ? styles.subRowDescText : undefined,
            ]}
          >
            {item?.nombre ?? ""}
          </Text>
        </View>

        <View style={[styles.dataCell, styles.wUnidad]}>
          <Text style={textStyle}>{item?.unidad ?? ""}</Text>
        </View>

        {renderNumberCell(
          styles,
          detalle?.montO_TOTCONT_CANTIDAD_CONCEPTO_PAGO,
          [styles.dataCell, styles.wScCant],
          textStyle,
        )}

        {renderNumberCell(
          styles,
          detalle?.montO_TOTCONT_PRECIO_UNITARIO,
          [styles.dataCell, styles.wScPU],
          textStyle,
        )}

        {renderNumberCell(
          styles,
          detalle?.montO_TOTCONT_MONTO_BASE_MC,
          [styles.dataCell, styles.wScTot],
          textStyle,
        )}

        {renderNumberCell(
          styles,
          detalle?.acuM_ANTERIOR_CANTIDAD_CONCEPTO_PAGO,
          [styles.dataCell, styles.wAmaCant],
          textStyle,
        )}

        {renderNumberCell(
          styles,
          detalle?.acuM_ANTERIOR_MONTO_PAGO_MC,
          [styles.dataCell, styles.wAmaTot],
          textStyle,
        )}

        {renderNumberCell(
          styles,
          detalle?.eP_MES_CANTIDAD_CONCEPTO_PAGO,
          [styles.dataCell, styles.wPaCant],
          textStyle,
          true,
        )}

        {renderNumberCell(
          styles,
          detalle?.eP_MES_MONTO_BASE_MC,
          [styles.dataCell, styles.wPaVmb],
          textStyle,
        )}

        {renderNumberCell(
          styles,
          detalle?.eP_MES_FACTOR_REAJUSTE,
          [styles.dataCell, styles.wPaFr],
          textStyle,
          false,
          2,
          4,
        )}

        {renderNumberCell(
          styles,
          detalle?.eP_MES_REAJUSTE_MC,
          [styles.dataCell, styles.wPaMr],
          textStyle,
        )}

        {renderNumberCell(
          styles,
          detalle?.eP_MES_MONTO_PAGO_MC,
          [styles.dataCell, styles.wPaTot],
          textStyle,
        )}

        {renderNumberCell(
          styles,
          detalle?.acuM_CANTIDAD_CONCEPTO_PAGO,
          [styles.dataCell, styles.wTaCant],
          textStyle,
        )}

        {renderNumberCell(
          styles,
          detalle?.acuM_MONTO_PAGO_MC,
          [styles.dataCell, styles.wTaTot],
          textStyle,
        )}
      </View>
    );
  };

  return (
    <View style={styles.table}>
      {/* Fila 1: encabezados de grupo */}
      <View style={[styles.row]} fixed>
        <View style={[styles.blankCell, styles.wItem, styles.rowSpanCell]}>
          <Text></Text>
        </View>

        <View style={[styles.blankCell, styles.wDesc, styles.rowSpanCell]}>
          <Text></Text>
        </View>

        <View style={[styles.blankCell, styles.wUnidad, styles.rowSpanCell]}>
          <Text></Text>
        </View>

        <View
          style={[
            styles.cell,
            styles.wScGroup,
            styles.hGroup,
            { backgroundColor: "#d0d0d0" },
          ]}
        >
          <Text>Según contrato</Text>
        </View>

        <View
          style={[
            styles.cell,
            styles.wAmaGroup,
            styles.hGroup,
            { backgroundColor: "#d0d0d0" },
          ]}
        >
          <Text>Acumulado mes anterior</Text>
        </View>

        <View
          style={[
            styles.cell,
            styles.wPaGroup,
            styles.hGroup,
            { backgroundColor: "#d0d0d0" },
          ]}
        >
          <Text>Período actual</Text>
        </View>

        <View
          style={[
            styles.cell,
            styles.wTaGroup,
            styles.hGroup,
            { backgroundColor: "#d0d0d0" },
          ]}
        >
          <Text>Total acumulado</Text>
        </View>
      </View>

      {/* Fila 2: subencabezados */}
      <View style={[styles.row, { backgroundColor: "#d0d0d0" }]} fixed>
        <View style={[styles.cell, styles.wItem, styles.rowSpanCell]}>
          <Text>Item</Text>
        </View>

        <View style={[styles.cell, styles.wDesc, styles.rowSpanCell]}>
          <Text>Descripción del Item</Text>
        </View>

        <View style={[styles.cell, styles.wUnidad, styles.rowSpanCell]}>
          <Text>Unidad</Text>
        </View>
        <View style={[styles.cell, styles.wScCant]}>
          <Text>Cantidad</Text>
        </View>
        <View style={[styles.cell, styles.wScPU]}>
          <Text hyphenationCallback={(word) => [word]}>Precio Unitario</Text>
        </View>
        <View style={[styles.cell, styles.wScTot]}>
          <Text>Valor total</Text>
        </View>

        <View style={[styles.cell, styles.wAmaCant]}>
          <Text>Cantidad</Text>
        </View>
        <View style={[styles.cell, styles.wAmaTot]}>
          <Text>Valor total</Text>
        </View>

        <View style={[styles.cell, styles.wPaCant]}>
          <Text>Cantidad</Text>
        </View>
        <View style={[styles.cell, styles.wPaVmb]}>
          <Text hyphenationCallback={(word) => [word]}>Valor moneda base</Text>
        </View>
        <View style={[styles.cell, styles.wPaFr]}>
          <Text>Factor reajuste</Text>
        </View>
        <View style={[styles.cell, styles.wPaMr]}>
          <Text>Monto reajuste</Text>
        </View>
        <View style={[styles.cell, styles.wPaTot]}>
          <Text>Valor total</Text>
        </View>

        <View style={[styles.cell, styles.wTaCant]}>
          <Text>Cantidad</Text>
        </View>
        <View style={[styles.cell, styles.wTaTot]}>
          <Text>Valor total</Text>
        </View>
      </View>

      {rows.map((partida) => (
        <View key={`group-${partida.partidA_ID}`}>
          {renderDataRow(partida, false)}
          {(partida?.sub_partidas ?? []).map((subPartida) =>
            renderDataRow(subPartida, true),
          )}
        </View>
      ))}
      {hashRows.map((row) => renderDataRow(row, false))}
    </View>
  );
}

export default ItemsTable;
